import { PropsWithChildren } from 'react';

import { APIProvider, Map, MapProps } from '@vis.gl/react-google-maps';
import { useId } from 'ui/Hooks';

import { useMapContext } from './context/useMapContext';

// Raleigh, NC
const DEFAULT_LOCATION = {
  lat: 35.787743,
  lng: -78.644257,
};

/**
 *
 * The MapApiProvider is a wrapper for the APIProvider from @vis.gl/react-google-maps
 * This provides the connection to the Google Maps API
 *
 */
const MapApiProvider = ({ children }: { children: React.ReactNode | React.ReactNode[] }) => (
  <APIProvider apiKey={import.meta.env.VITE_GOOGLE_API_KEY}>{children}</APIProvider>
);

interface MapContainerProps extends PropsWithChildren<MapProps> {
  id: string;
  /**
   * If true, the MapProvider will be included in the tree
   * This is useful when you are rendering a siloed map that does not need to interact
   * components outside of the map.
   *
   * When false you must wrap the map in a MapProvider higher in the tree
   *
   * @default true
   */
  withProvider?: boolean;
  defaultLocation?: { lat: number; lng: number };
}

/**
 * Removes the local Id from MapProps
 * This is necessary because the Map component will consume the id
 * that is provided by the MapProvider
 */
type MapPropsWithoutId = Omit<MapProps, 'id'>;
interface _MapProps
  extends PropsWithChildren<MapPropsWithoutId>,
    Pick<MapContainerProps, 'defaultLocation'> {
  id?: never;
}

const Map_ = ({ children, defaultLocation = DEFAULT_LOCATION, ...rest }: _MapProps) => {
  const { id } = useMapContext();

  /**
   * The Map ID of the map. This parameter cannot be set or changed after a map is instantiated.
   */
  const mapId = useId('default'); // Currently we have no need to have different map ids

  return (
    <Map
      defaultZoom={12}
      gestureHandling={'greedy'}
      disableDefaultUI={true}
      {...rest}
      defaultCenter={defaultLocation}
      id={id}
      mapId={mapId}
      reuseMaps={true}
      clickableIcons={false}
    >
      {children}
    </Map>
  );
};

/**
 * Returns the Map and optionally wraps it with the
 * MapProvider to provide the map context
 */
// const MapContainer = ({ id, children, withProvider = true, ...rest }: MapContainerProps) => {
//   const _id = useId(id);

//   return (
//     <ConditionalWrapper
//       condition={withProvider}
//       wrapper={(children) => (
//         <MapProvider
//           id={_id}
//           latlng={{
//             lat: DEFAULT_LOCATION.lat,
//             lng: DEFAULT_LOCATION.lng,
//           }}
//         >
//           <Map_ {...rest}>{children}</Map_>
//         </MapProvider>
//       )}
//     >
//       <Map_ {...rest}>{children}</Map_>
//     </ConditionalWrapper>
//   );
// };
export { Map_ as Map, MapApiProvider };
